import type { HTMLAttributes, ReactNode } from "react";
import { motion, useReducedMotion } from "motion/react";
import { cn } from "../lib/cn";
import { durations, easings } from "@andrum/design-tokens";
import { PageLoader, ScreenSkeleton } from "./page-loader";
export type ScreenProps = HTMLAttributes<HTMLDivElement> & {
  children?: ReactNode;
  /** Extra bottom padding when bottom nav is present */
  withBottomNav?: boolean;
  /**
   * Soft entrance. Prefer false when AppShell already crossfades routes
   * (avoids double-jump).
   */
  animate?: boolean;
  /** Show calm loader while feature data hydrates */
  loading?: boolean;
  /** Skeleton placeholders instead of spinner */
  skeleton?: boolean;
  loadingLabel?: string;
  /**
   * Fill the shell viewport.
   * Default true so screens fit the fixed app chrome.
   */
  fill?: boolean;
  /**
   * How overflow is handled when fill is true:
   * - "page" (default): the screen itself scrolls if content is taller than the viewport.
   * - "lock": no outer scroll — use an inner ScrollRegion for lists/chat bodies.
   *
   * Prefer denser layouts that fit without scrolling; page/lock always remain
   * scroll-safe so content is never silently clipped.
   */
  scrollMode?: "page" | "lock";
  /**
   * @deprecated Use scrollMode="page" (default) or scrollMode="lock".
   * true → page, false → lock.
   */
  scroll?: boolean;
};

/**
 * App screen frame. Content taller than the viewport must always be reachable
 * via scroll (page mode or an inner ScrollRegion when locked).
 */
export function Screen({
  children = null,
  className,
  withBottomNav = false,
  animate = false,
  loading = false,
  skeleton = false,
  loadingLabel = "andrum",
  fill = true,
  scrollMode,
  scroll,
  ...props
}: ScreenProps) {
  const reduce = useReducedMotion();
  const mode: "page" | "lock" =
    scrollMode ?? (scroll === false ? "lock" : "page");

  const classes = cn(
    "mx-auto w-full max-w-lg",
    "px-5 sm:px-6",
    fill
      ? mode === "page"
        ? "flex h-full min-h-0 flex-col overflow-x-hidden overflow-y-auto overscroll-y-contain pt-3 pb-5 space-y-3.5 [-webkit-overflow-scrolling:touch]"
        : "flex h-full min-h-0 flex-col overflow-hidden pt-3 pb-4 space-y-3.5"
      : "pt-6 pb-8 space-y-6",
    withBottomNav && !fill && "pb-8",
    className
  );

  const body = loading ? (
    skeleton ? (
      <ScreenSkeleton className="px-0 pt-0" />
    ) : (
      <PageLoader label={loadingLabel} />
    )
  ) : (
    children
  );

  if (!animate || reduce) {
    return (
      <div className={classes} {...props}>
        {body}
      </div>
    );
  }

  return (
    <motion.div
      className={classes}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{
        duration: durations.fast,
        ease: easings.enter,
      }}
      {...(props as object)}
    >
      {body}
    </motion.div>
  );
}
